import React from 'react'
import { Building2, Settings, Package, Container, ArrowRight, ExternalLink } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Button } from '@wove/web-ui'
import { useIntegrations } from '@wove/api'
import AddIntegration from './AddIntegration'

export default function OverviewPage() {
  const { integrations, isLoading, error } = useIntegrations()

  if (error) {
    return (
      <div className="p-6 max-w-6xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Organization Settings</h1>
          <p className="text-lg text-gray-600">
            Configure organization-wide settings and manage integration-specific configurations.
          </p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error Loading Integrations
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>Unable to load your integrations. Please try refreshing the page.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Organization Settings</h1>
        <p className="text-lg text-gray-600">
          Configure organization-wide settings and manage integration-specific configurations.
        </p>
      </div>

      {/* Integrations List */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <h2 className="text-2xl font-bold text-gray-900">Integrations</h2>
            <div className="ml-4 px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
              {isLoading ? 'Loading...' : `${integrations.length} Integration${integrations.length !== 1 ? 's' : ''}`}
            </div>
          </div>
          <AddIntegration />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading integrations...</span>
          </div>
        ) : integrations.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  No Integrations Found
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>You need to set up at least one integration before you can configure integration-specific settings.</p>
                </div>
                <div className="mt-4 flex space-x-3">
                  <AddIntegration 
                    trigger={
                      <Button className="text-white">
                        Add Your First Integration
                      </Button>
                    }
                  />
                  <Button
                    variant="outline"
                    onClick={() => window.location.href = '/'}
                    className="text-yellow-700 border-yellow-300 hover:bg-yellow-100"
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {integrations.map((integration) => (
              <div
                key={integration.id}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {integration.name}
                    </h3>
                    <p className="text-sm text-gray-500 mb-2">
                      {integration.integrationType} • ID: {integration.id}
                    </p>
                  </div>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <Settings className="h-4 w-4 mr-2 text-blue-600" />
                    Custom Fields
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Package className="h-4 w-4 mr-2 text-green-600" />
                    Unit Types
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <Container className="h-4 w-4 mr-2 text-purple-600" />
                    Container Types
                  </div>
                </div>

                <Link to={`/integrations/${integration.id}`}>
                  <Button variant="outline" className="w-full group">
                    Configure Settings
                    <ExternalLink className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}